import _ from "lodash"

import {
  CruiseLineScopedFields,
  ReservedFields,
  SettlementMethods,
  TourLocation,
} from "@/onboarding/features/product/models/Product"
import { BidStatuses } from "@/onboarding/features/product/models/Bid"
import { nullField } from "@/onboarding/features/product/models/Field"
import * as AsyncField from "@/helpers/AsyncField"
import { isBlank } from "@/utils"

export const getState = ({ product }) => product

export const getView = (state) => getState(state)._view.value

export const getProductId = (state) => getState(state).productId

export const getAuthDescriptor = (state) => getState(state).authDescriptor

export const getFormsEditable = (state) => getProduct(state).canEdit

export const getFormsExpanded = (state) => getState(state).formsExpanded

export const getForm = (state) => getView(state).form

export const getProduct = (state) => getView(state).product

export const getDefaultRateCategories = (state) => getProduct(state).defaultRateCategories

export const getDisplayedNetRateCategories = (state) => getProduct(state).displayedNetRateCategories

export const getDefaultTiers = (state) => getProduct(state).defaultTiers

export const getReferenceOptions = (state) => getView(state).referenceOptions

export const getProductFilters = (state) => {
  const form = getForm(state)

  // Use the selected product under the season selector if applicable
  const productId = isBlank(form.seasonProductId.value)
    ? getProductId(state)
    : form.seasonProductId.value

  return {
    id: productId,
    bidId: form.bidId.value,
    contractPeriodId: form.contractPeriodId.value,
    detailsConfirmationId: form.detailsConfirmationId.value,
    applicableProductIds: _.map(form.applicableProducts, "value"),
  }
}

export const getProductMainDetails = (state) => getProduct(state).productMainDetailOptions

export const getInternalTag = (state) => getProduct(state).internalTag

export const getSectionChat = (section) => (state) => {
  const form = getForm(state)
  const bidId = form.bidId.value
  const bid = getBid(bidId)(state)
  const product = getProduct(state)

  const bidChat = bid ? bid.chats.find((chat) => chat.section === section) : null
  const productChat = product ? product.chats.find((chat) => chat.section === section) : null

  return bidChat || productChat || null
}

export const getContractPeriodId = (state) => getForm(state).contractPeriodId.options

export const isProductStale = (state) => AsyncField.isStale(getState(state)._view)

export const isProductReady = (state) => AsyncField.isReady(getState(state)._view)

// Null-safe product lookup for selectors that must also work on pages with
// no product slice mounted (Component Library) — getProduct assumes the
// slice and view are present and would throw there.
const resolveProduct = (state) => getState(state)?._view?.value?.product

export const isProductIdea = (state) => isBlank(getProduct(state).season.id)

// Whether a field that is hidden outright on an operator tour idea
// (models/Product CruiseLineScopedFields, currently just Meeting Location) is
// visible: hidden while the viewer is an operator looking at their own tour
// idea (the read model's viewer-aware isOperatorTourIdea feature), shown
// otherwise, including on a cruise line's own seasonless tour-library tour
// (SHO-TSK-967). Every other cruise-line-scoped field stays visible and shows
// a placeholder instead — see CRUISE_LINE_FIELD_PLACEHOLDER. Accepts camelCase
// or snake_case names; a name outside that schema list throws — visibility on
// any other axis is the template's business (templateRenders and friends).
// Defaults to visible when no product slice is mounted (Component Library).
export const fieldVisible =
  ({ name }: { name: string }) =>
  (state): boolean => {
    if (!CruiseLineScopedFields.has(_.snakeCase(name))) {
      throw new Error(`fieldVisible: "${name}" is not a cruise-line-scoped schema field`)
    }
    const product = resolveProduct(state)
    return product == null || !product.features.general.product.isOperatorTourIdea
  }

export const isOperatorTourIdea = (state) => getFeatures(state).general.product.isOperatorTourIdea

export const getFeatures = (state) => getProduct(state).features

export const canEditProduct = (state) => getFeatures(state).general.product.editable

export const isOverriddenNetRatesheetHidden = (state) =>
  getFeatures(state).general.product.hideOverriddenNetRatesheet

export const isCalculatedNetRatesheetHidden = (state) =>
  getFeatures(state).general.product.hideCalculatedNetRatesheet

export const getBids = (state) => getProduct(state).bids

export const getBid = (bidId) => (state) => _.find(getBids(state), ({ id }) => id === bidId)

export const canEditBid = (bidId) => (state) => {
  const authDescriptor = getAuthDescriptor(state)
  const bid = getBid(bidId)(state)

  if (!bid) {
    return false
  }

  return (
    authDescriptor.isTourUi &&
    authDescriptor.companyId === bid.operator.id &&
    (bid.status === BidStatuses.REQUESTED || bid.status === BidStatuses.DRAFT)
  )
}

export const getActiveBid = (state) => {
  const editingProduct = canEditProduct(state)

  if (editingProduct) {
    return null
  }

  return getBid(getProductFilters(state).bidId)(state)
}

export const canEditActiveBid = (state) => {
  const activeBid = getActiveBid(state)

  if (!activeBid) {
    return false
  }

  return canEditBid(activeBid.id)(state)
}

// True when the active bid is a submitted operator bid under cruise-line review.
// Used to lock cruise-line/owner editing of CL-owned sections during review while
// leaving them editable for contracting/contracted tours (no active bid, or an
// awarded bid).
export const isBidUnderReview = (state) => getActiveBid(state)?.status === BidStatuses.SUBMITTED

export const canEdit = (state) => {
  const editProduct = canEditProduct(state)
  const editActiveBid = canEditActiveBid(state)

  return editProduct || editActiveBid
}

// Cost-table editability. Like canEdit, but the product-side edit right is the
// Cost-Manager-gated edit_costs flag rather than the general editable
// flag. The bid path is unchanged, so operators editing their bid costs are
// unaffected.
export const canEditCosts = (state) => {
  // Read-model feature keys are camelized on the client (edit_costs -> editCosts).
  const { editCosts } = getFeatures(state).general.product
  const editActiveBid = canEditActiveBid(state)

  return Boolean(editCosts) || editActiveBid
}

export const getFieldUpdateContext = (state) => _.omitBy(getProductFilters(state), _.isNil)

export const getMediaItems = (state) => getProduct(state).mediaItems

export const getOriginalMediaItems = (state) => getProduct(state).originalMediaItems

export const getAcceptedMediaFormats = (state) => getProduct(state).acceptedMediaFormats

export const getMediaDisclaimerText = (state) => getProduct(state).mediaDisclaimerText

export const getTimeline = (state) => getProduct(state).segments

export const getOriginalStops = (state) => {
  const fieldSettings = getSettings(ReservedFields.TOUR_STOPS)(state)
  const originalTourStops = fieldSettings[ReservedFields.TOUR_STOPS]?.previousValue || []

  const { netCurrencyCode } = getProduct(state)

  return originalTourStops.map((stop) => ({
    components: [],
    ...stop,
    fieldName: originalTourStops.name,
    currency: netCurrencyCode,
  }))
}

export const getOriginalStopComponents = (state) => {
  const originalStops = getOriginalStops(state)

  const stopsWithDefaultNames = originalStops.map((stop, stopIndex) => {
    const stopName = stop.name.trim() === "" ? `Segment ${stopIndex + 1}` : stop.name

    const updatedComponents = stop.components.map((component, componentIndex) => {
      const componentName =
        component.name.trim() === "" ? `Component ${componentIndex + 1}` : component.name
      return { ...component, name: componentName }
    })

    return { ...stop, name: stopName, components: updatedComponents }
  })

  return stopsWithDefaultNames
}

export const getStops = (state) => {
  const { netCurrencyCode } = getProduct(state)
  const field = getField(ReservedFields.TOUR_STOPS)(state)

  return field.value.map((stop) => ({
    components: [],
    ...stop,
    fieldName: field.name,
    currency: netCurrencyCode,
  }))
}

export const getStopsWithComponents = (state) => {
  const stops = getStops(state)

  const stopsWithDefaultNames = stops.map((stop, stopIndex) => {
    const stopName = stop.name.trim() === "" ? `Segment ${stopIndex + 1}` : stop.name

    const updatedComponents = stop.components.map((component, componentIndex) => {
      const componentName =
        component.name.trim() === "" ? `Component ${componentIndex + 1}` : component.name
      return { ...component, name: componentName }
    })

    return { ...stop, name: stopName, components: updatedComponents }
  })

  return stopsWithDefaultNames
}

export const getLinkableComponentOptions = (state) =>
  [
    ReservedFields.LOGISTICAL_COMPONENTS,
    ReservedFields.VENUE_COMPONENTS,
    ReservedFields.REFRESHMENT_COMPONENTS,
    ReservedFields.RESTAURANT_COMPONENTS,
    ReservedFields.ENTERTAINMENT_COMPONENTS,
    ReservedFields.GUIDE_COMPONENTS,
    ReservedFields.HEADSET_COMPONENTS,
    ReservedFields.OVERHEAD_CATEGORIES,
  ].flatMap((fieldName) =>
    getField(fieldName)(state).value.map((component, index) => {
      const componentName = isBlank(component.name) ? `Component ${index + 1}` : component.name

      return { value: component.id, label: `${componentName} (${_.startCase(component.kind)})` }
    })
  )

export const getSectionFieldNames = {
  financialDetails: (state) => {
    const { isTourUi } = getAuthDescriptor(state)

    return isTourUi
      ? [ReservedFields.NET_RATESHEET]
      : [
          ReservedFields.TARGET_ADULTS,
          ReservedFields.TARGET_CHILDREN,
          ReservedFields.TARGET_SALES,
          ReservedFields.TARGET_MARGIN,
          ReservedFields.RACK_RATESHEET,
          ReservedFields.NET_RATESHEET,
        ]
  },
  description: (state) => getProduct(state).descriptionFieldNames,
  tourDetails: (state) => getProduct(state).tourDetailsFieldNames,
  additionalDetails: (state) => getProduct(state).additionalDetailsFieldNames,
  images: () => [ReservedFields.TOUR_IMAGES],
  stops: () => [ReservedFields.TOUR_STOPS],
  tourLocation: () => [
    TourLocation.START_LOCATION,
    TourLocation.TOUR_DURATION,
    ReservedFields.MEETING_LOCATION,
    ReservedFields.MEETING_TIME_BUFFER_ANCHORED,
    ReservedFields.MEETING_TIME_BUFFER_DOCKED,
  ],
  logistics: () => [ReservedFields.LOGISTICAL_COMPONENTS],
  overhead: () => [ReservedFields.OVERHEAD_CATEGORIES],
  priceVariance: () => [],
  extraFees: () => [ReservedFields.EXTRA_FEES],
}

export const getAllFieldNames = (state) => _.flatMap(getSectionFieldNames, (fn) => fn(state))

export const hasField = (fieldName) => (state) => _.includes(getAllFieldNames(state), fieldName)

export const getFinancialDetailFields = (state) => {
  const fieldNames = getSectionFieldNames.financialDetails(state)

  return getFieldValues(...fieldNames)(state)
}

export const getTargetPaxFieldNames = (state) => {
  // TODO Switch to dynamic pax types here
  const paxTypeFields = [ReservedFields.TARGET_ADULTS, ReservedFields.TARGET_CHILDREN]

  const financialFields = getSectionFieldNames.financialDetails(state)

  return paxTypeFields.filter((field) => _.includes(financialFields, field))
}

export const getTargetRevenueFieldNames = (state) => {
  const financialFields = getSectionFieldNames.financialDetails(state)

  return [ReservedFields.TARGET_SALES, ReservedFields.TARGET_MARGIN].filter((field) =>
    _.includes(financialFields, field)
  )
}

export const isComponentImportable = (fieldName: string) => (state) => {
  if (fieldName == ReservedFields.ATTACHMENTS || fieldName == ReservedFields.TOUR_IMAGES) {
    return false
  }

  const field = getProductField(fieldName)(state)

  const editable = field.writeable && canEdit(state)
  const features = getFeatures(state)

  return editable && features.toolsDropdown.importComponent
}

export const getProductField =
  (fieldName: string, filter = {}) =>
  (state) => {
    const { primaryDocument } = getProduct(state)

    const field =
      _.find(primaryDocument.fields, ({ name }) => _.snakeCase(name) === _.snakeCase(fieldName)) ||
      nullField(fieldName)

    const fieldSettings = getView(state).product.fieldSettings.fields
    const fieldSetting = _.find(
      fieldSettings,
      ({ name }) => _.snakeCase(name) === _.snakeCase(fieldName)
    )

    if (!fieldSetting) {
      return field
    }

    if (fieldSetting.writeable === field.writeable) {
      return field
    }

    return { ...field, writeable: fieldSetting.writeable }
  }

// True if the named field is actually present on the product's primary
// document (as opposed to merely being known to the schema). Use this to
// gate UI for fields that may be absent on legacy/un-backfilled products.
export const hasProductField =
  (fieldName: string) =>
  (state): boolean => {
    const { primaryDocument } = getProduct(state)
    return _.some(
      primaryDocument.fields,
      ({ name }) => _.snakeCase(name) === _.snakeCase(fieldName)
    )
  }

// True if the active tour-product template UNCONDITIONALLY renders
// +fieldName+ in the given area ("primary", "venue", "transport",
// "refreshment", "restaurant", etc). Driven by the template render policy
// serialised from Ruby — see docs/tour-product-templates.md §3. Crucially,
// this does NOT hide on data absence: the template is the authority, not
// the document.
//
// Sibling +templateRendersUnlessPresent+ covers the third policy state
// (hide_unless_present) where the template defers to value presence.
//
// Resolution order: the loaded product's template (tour views) first,
// then the standalone templateSlice payload (Component Library) — the
// controller ships the current company's active template into
// @react_params.template, ComponentCreateApp/ComponentEditorApp dispatch
// it into state.template on mount. Fallback TRUE only when neither
// source of context is present (e.g. legacy or test contexts) so the
// schema designer still sees fields when no template is wired.
export const templateRenders =
  (area: string, fieldName: string) =>
  (state): boolean => {
    if (!hasTemplateContext(state)) return true
    const template = resolveTemplate(state)
    if (!template?.renders) return false
    const fields = template.renders[area]
    return Array.isArray(fields) && fields.includes(fieldName)
  }

// True if the active template flagged +fieldName+ as :hide_unless_present
// in +area+. The render decision is then up to the caller, which should
// combine this with a value-presence check on its own field data, e.g.:
//
//   const showsRaw      = useSelector(templateRenders("restaurant", "vegetarian_meals"))
//   const showsIfHasVal = useSelector(templateRendersUnlessPresent("restaurant", "vegetarian_meals"))
//   const showField     = showsRaw || (showsIfHasVal && isNonBlank(restaurant.vegetarian_meals))
//
// For primary-area fields, the value lookup goes through getField. For
// component-area fields (restaurant, venue, etc.), each component
// instance has its own values; the caller resolves "present" against
// the specific instance being rendered.
export const templateRendersUnlessPresent =
  (area: string, fieldName: string) =>
  (state): boolean => {
    const template = resolveTemplate(state)
    if (!template?.renders_unless_present) return false
    const fields = template.renders_unless_present[area]
    return Array.isArray(fields) && fields.includes(fieldName)
  }

// Resolve the active template from either the loaded product view (tour
// context) or the standalone templateSlice payload (Component Library).
// If a product view is loaded, its template is authoritative — even if
// the template field itself is missing (that maps to "everything hidden"
// via the strict downstream checks). The standalone slice only applies
// when no product view is present at all.
const resolveTemplate = (state): any => {
  const product = resolveProduct(state)
  if (product) return product.template ?? null
  return state?.template?.payload ?? null
}

// True if any template context is available — a loaded product view or a
// standalone templateSlice payload. When false, the selectors default to
// the permissive schema-designer fallback (everything shows).
const hasTemplateContext = (state): boolean =>
  resolveProduct(state) != null || state?.template?.payload != null

// "Non-blank" for the purpose of templateRendersUnlessPresent.
// Treats nil, the empty string, and the empty array as blank — supports
// "no value ever set", "operator deliberately cleared", and "multi-select
// with nothing selected". Booleans (true/false) and other primitives are
// non-blank — false is a meaningful operator answer that should preserve
// display.
export const isNonBlank = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return false
  if (Array.isArray(value) && value.length === 0) return false
  return true
}

// Combined selector for the common pattern: render the field if the
// template unconditionally shows it OR the template defers to value
// presence and the caller's value is non-blank. Use this for COMPONENT-AREA
// fields (restaurant, venue, etc) where each component instance has its
// own values — the caller passes the relevant instance's value, e.g.
//
//   const showVegetarianMeals = useSelector(
//     templateRendersFieldWithValue("restaurant", "vegetarian_meals",
//                                   values.vegetarianMeals)
//   )
//
// For primary-area fields use +templatePrimaryFieldRenders+, which looks
// the value up internally from the product's primaryDocument.
export const templateRendersFieldWithValue =
  (area: string, fieldName: string, value: unknown) =>
  (state): boolean =>
    templateRenders(area, fieldName)(state) ||
    (templateRendersUnlessPresent(area, fieldName)(state) && isNonBlank(value))

// All-in-one selector for primary-area fields: combines the template
// policy with a value lookup against the primary document. Replaces
// the common pattern of +templateRenders("primary", fieldName)+ with
// value-aware resolution for hide_unless_present fields.
//
//   const showTimeOfDay = useSelector(templatePrimaryFieldRenders("time_of_day"))
export const templatePrimaryFieldRenders =
  (fieldName: string) =>
  (state): boolean => {
    if (templateRenders("primary", fieldName)(state)) return true
    if (!templateRendersUnlessPresent("primary", fieldName)(state)) return false
    const field = getField(fieldName)(state)
    return isNonBlank(field?.value)
  }

// Like +templatePrimaryFieldRenders+, but in bid context additionally
// requires the field to exist on the active document. Bid documents are
// immutable, biddable-filtered snapshots, so template-promoted non-biddable
// fields (tour_destination, time_of_day, ...) are absent there — rendering
// them in bid context submits values the update pipeline rejects with
// FieldNotFound. Master documents need no presence check: legacy documents
// created before a field's schema promotion lazily migrate on the next save
// (ProductService#migrate_primary_document_schema!), so template policy
// alone decides and the first edit materialises the field.
export const templatePrimaryDocumentFieldRenders =
  (fieldName: string) =>
  (state): boolean => {
    if (!templatePrimaryFieldRenders(fieldName)(state)) return false
    if (isBlank(getForm(state).bidId.value)) return true
    return hasProductField(fieldName)(state)
  }

export const getFields =
  (...fieldNames: string[]) =>
  (state): Record<string, any> =>
    _.reduce(
      fieldNames,
      (values, fieldName) => ({
        ...values,
        [fieldName]: getField(fieldName)(state),
      }),
      {}
    )

export const getFieldValues =
  (...fieldNames: string[]) =>
  (state): Record<string, any> =>
    _.reduce(
      fieldNames,
      (values, fieldName) => ({
        ...values,
        [fieldName]: getField(fieldName)(state).value,
      }),
      {}
    )

export const getSettings =
  (...fieldNames: string[]) =>
  (state): Record<string, any> => {
    const fieldSettings = getView(state).product.fieldSettings.fields

    return _.reduce(
      fieldNames,
      (values, fieldName) => {
        const fieldSetting = _.find(fieldSettings, { name: fieldName })

        return {
          ...values,
          [fieldName]: fieldSetting || null,
        }
      },
      {}
    )
  }

export const getSectionSettings =
  (sectionKey: string) =>
  (state): any => {
    const sectionSettings = getFeatures(state).general.product.sectionSettings.sections
    return _.find(sectionSettings, { key: sectionKey }) || null
  }

export const countIncompleteRequiredFields =
  (...fieldNames: string[]) =>
  (state): number => {
    const fieldsSettings = getSettings(...fieldNames)(state)

    let count = 0

    Object.values(fieldsSettings).forEach((field) => {
      if (field?.required && field?.incomplete) {
        count += 1
      }
    })

    return count
  }

export const countChangedFields =
  (...fieldNames: string[]) =>
  (state): number => {
    const fieldsSettings = getSettings(...fieldNames)(state)
    let count = 0

    Object.values(fieldsSettings).forEach((field) => {
      if (field?.changed) {
        count += 1
      }
    })

    return count
  }

export const getPerformanceSummary = (state) => {
  const { dailySummary, seasonSummary } = getProduct(state)

  return {
    daily: dailySummary,
    season: seasonSummary,
  }
}

// TODO Apply filters to search for ship and cruise version
export const getBidField =
  (bidId: string, filter = {}) =>
  (fieldName: string) =>
  (state) => {
    const {
      document: { fields },
    } = getBid(bidId)(state)

    return (
      _.find(fields, ({ name }) => _.snakeCase(name) === _.snakeCase(fieldName)) ||
      nullField(fieldName)
    )
  }

export const getField = (fieldName: string) => (state) => {
  const filter = getProductFilters(state)
  const bid = getActiveBid(state)

  const productField = getProductField(fieldName, filter)(state)

  if (bid) {
    const bidField = getBidField(bid.id, filter)(fieldName)(state)

    // TODO Calling this the rootField might not be wise. I essentially want the field that
    //      I will be using to display the original product values when viewing this from
    //      the perspective of a bid.
    return {
      ...bidField,
      rootField: productField,
    }
  }

  return productField
}

export const getLogisticalComponents = (state) => {
  const { netCurrencyCode } = getProduct(state)
  const field = getField(ReservedFields.LOGISTICAL_COMPONENTS)(state)
  const {
    dailySummary: { expectedGuests },
  } = getProduct(state)

  return field.value.map((component) => ({
    ...component,
    usage: _.ceil(expectedGuests / (component.capacity || 0)) || 0,
    fieldName: field.name,
    currency: netCurrencyCode,
  }))
}

export const getOverheadCategories = (state) => {
  const { netCurrencyCode } = getProduct(state)
  const field = getField(ReservedFields.OVERHEAD_CATEGORIES)(state)

  return field.value.map((category) => ({
    ...category,
    percentage: getBuyRatePercentage(
      category.ratesheet.rates.adult,
      category.ratesheet.rates.child
    )(state),
    fieldName: field.name,
    currency: netCurrencyCode,
  }))
}

export const getOriginalOverheadCategories = (state) => {
  const { netCurrencyCode } = getProduct(state)
  const fieldSettings = getSettings(ReservedFields.OVERHEAD_CATEGORIES)(state)
  const originalOverheadCategories =
    fieldSettings[ReservedFields.OVERHEAD_CATEGORIES]?.previousValue || []
  return originalOverheadCategories.map((category) => ({
    ...category,
    percentage: getBuyRatePercentage(
      category.ratesheet.rates.adult,
      category.ratesheet.rates.child
    )(state),
    fieldName: originalOverheadCategories.name,
    currency: netCurrencyCode,
  }))
}

// TODO This should eventually check the ratesheet type and use target pax to get rates
export const getTargetBuyRates = (state) => {
  const {
    dailySummary: { currentCosts },
  } = getProduct(state)
  const { target_adults, target_children } = getFieldValues(
    ReservedFields.TARGET_ADULTS,
    ReservedFields.TARGET_CHILDREN
  )(state)

  const total = target_adults + target_children

  if (total === 0) {
    return {
      adult: 0,
      child: 0,
    }
  }

  // TODO Taking the ratio here is technically not right, but this becomes very complicated
  // with tiered rates
  return {
    adult: currentCosts.fractional / total,
    child: currentCosts.fractional / total,
  }
}

export const getBuyRatePercentage = (adultRate, childRate) => (state) => {
  const {
    dailySummary: { currentCosts },
  } = getProduct(state)
  const { target_adults, target_children } = getFieldValues(
    ReservedFields.TARGET_ADULTS,
    ReservedFields.TARGET_CHILDREN
  )(state)

  const cost = adultRate * target_adults + childRate * target_children

  return cost / currentCosts.fractional || 0
}

export const getVehicles = (state) => {
  const { value } = getField(ReservedFields.VEHICLES)(state)

  return value
}

export const getComments = (sectionName) => (state) => {
  const { sectionComments } = getProduct(state)

  return _.filter(sectionComments, (comment) => comment.sectionName === sectionName)
}

export const getProductNetCurrencyCode = (state) => getProduct(state).netCurrencyCode

export const getProductRackCurrencyCode = (state) => getProduct(state).rackCurrencyCode

export const getCurrencyOptions = (state) => getProduct(state).currencyOptions

export const getSettlementMethod = (state) =>
  getField(ReservedFields.SETTLEMENT_METHOD)(state).value

export const isVehicleBasedSettlement = (state) => {
  const settlementMethod = getSettlementMethod(state)

  return (
    settlementMethod === SettlementMethods.PER_VEHICLE ||
    settlementMethod === SettlementMethods.PER_VEHICLE_AVERAGE
  )
}

export const getOptionsForReferenceType = (type: string) => (state) =>
  getReferenceOptions(state)[type]

export const getComponentTender = (componentId: string) => (state) =>
  getProduct(state).componentTenders[componentId]
